import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "./supabase.ts";
import { allSessions, mutateSession, recordSyncError, type LocalSession } from "./queue.ts";
import { drain } from "./sync.ts";
import { waitingRows } from "./prune.ts";

// The sync loop for the whole app, not for one screen.
//
// It used to live inside the tracker and drained only the session on screen, so
// anything still queued when a session ended without signal, or when the page
// reloaded, was never sent: nothing else ever called drain again. Code review of
// PR #3 found it from three separate angles.
//
// Runs on sign-in, when the browser reports it is back online, when the tab
// becomes visible again (a phone returning from the lock screen), and every 30
// seconds. drain is single flight per session, so these overlapping triggers
// cost nothing extra.

export interface SyncOverview {
  /** Rows on this phone not yet on the server, across this person's sessions. */
  waiting: number;
  /** Sessions ended on this phone whose end the server has not been told about. */
  endsWaiting: number;
  startsWaiting: number;
  /** This person's sessions that were never ended, except the one on screen. */
  unfinished: LocalSession[];
  /** When the queue was read for these numbers. A screen that must not claim
      "uploaded" on stale numbers compares this with its own moment. */
  checkedAt: number;
  pending: LocalSession[];
  unverifiedOwners: number;
  uploading: boolean;
  /** The phone's storage could not be read, so the numbers above say nothing.
      A failed read used to publish zeros, which read as "everything uploaded". */
  unreadable: boolean;
}

const EMPTY: SyncOverview = { waiting: 0, endsWaiting: 0, startsWaiting: 0, unfinished: [], checkedAt: 0, unreadable: false, pending: [], unverifiedOwners: 0, uploading: false };

// Records written before 2026-09-17 carry no owner, and they used to count as
// everyone's: a second person signing in on the same phone was offered the
// first person's session to resume. The server knows whose each session is,
// and only shows a person their own unless they are an admin, so ask it once
// and write the answer onto the record.
//
// A session the server does not return (deleted there, or someone else's) used
// to be asked about again every 30 seconds for as long as the phone kept it
// (EFF-1). An answer of nothing is remembered for a day, for the person who
// asked, before asking again.
const ASK_AGAIN_MS = 24 * 60 * 60 * 1000;

async function adoptOwners(sessions: LocalSession[], userId: string) {
  const now = Date.now();
  const askedRecently = (s: LocalSession) =>
    s.ownerChecked?.by === userId && now - s.ownerChecked.at <= ASK_AGAIN_MS;
  const unowned = sessions.filter((s) => !s.observerId && !askedRecently(s)).map((s) => s.id);
  if (unowned.length === 0) return;
  const { data, error } = await supabase
    .from("observation_sessions").select("id,observer_id").in("id", unowned);
  if (error || !data) return;
  const answered = new Set<string>();
  for (const row of data) {
    answered.add(row.id as string);
    await mutateSession(row.id, null, (s) => ({ ...s, observerId: row.observer_id as string }));
  }
  for (const id of unowned) {
    if (!answered.has(id)) await mutateSession(id, null, (s) => ({ ...s, ownerChecked: { by: userId, at: now } }));
  }
}

export function useBackgroundSync(userId: string | null, onScreenSessionId: string | null): [SyncOverview, () => void] {
  const [overview, setOverview] = useState<SyncOverview>(EMPTY);

  // Who is signed in now. A run that began under someone else stops at its next
  // step, rather than sending or showing that person's sessions under this
  // sign-in. Declared before the loop's effect so it updates first.
  const current = useRef(userId);
  useEffect(() => {
    current.current = userId;
    setOverview(EMPTY);
  }, [userId]);

  const running = useRef(new Set<string>());
  const again = useRef(new Set<string>());
  const readVersion = useRef(0);
  const refreshOverview = useCallback(async () => {
    if (!userId) return null;
    const version=++readVersion.current;
    const stillCurrent=()=>current.current===userId && readVersion.current===version;
    const checkedAt=Date.now();
    try {
      const all=await allSessions();
      const sessions=all.filter(s=>s.observerId===userId);
      const needsUpload=(s:LocalSession)=>waitingRows(s)>0 || (s.endedAt!==null && !s.endSyncedAt) || s.startPending;
      if(stillCurrent())setOverview({
        waiting:sessions.reduce((n,s)=>n+waitingRows(s),0),
        endsWaiting:sessions.filter(s=>s.endedAt!==null && !s.endSyncedAt).length,
        startsWaiting:sessions.filter(s=>s.startPending).length,
        unfinished:sessions.filter(s=>s.endedAt===null && s.id!==onScreenSessionId),
        pending:sessions.filter(needsUpload),
        unverifiedOwners:all.filter(s=>!s.observerId && needsUpload(s)).length,
        checkedAt,unreadable:false,uploading:running.current.has(userId),
      });
      return sessions;
    } catch {
      if(stillCurrent())setOverview(o=>({...o,checkedAt,unreadable:true,uploading:running.current.has(userId)}));
      return null;
    }
  },[userId,onScreenSessionId]);

  const run = useCallback(async (upload = true) => {
    if (!userId) return;
    // Publish saved state before any network wait. A slow earlier session must
    // not hide other pending rows, errors or recovery downloads.
    await refreshOverview();
    if(!upload || !navigator.onLine || current.current!==userId)return;
    if(running.current.has(userId)){again.current.add(userId);return;}
    running.current.add(userId);
    try {
      await refreshOverview();
      await adoptOwners(await allSessions(),userId);
      const sessions=(await refreshOverview())??[];
      const pending=sessions.filter(s=>!s.startPending && (waitingRows(s)>0 || (s.endedAt!==null && !s.endSyncedAt)));
      // At most two attempts per session, with bounded parallelism. A refused
      // or stalled historical record must not starve all later saved sessions.
      // One short retry also recovers a lost response without waiting 30s for
      // the next sweep. Semantic rejections remain visible, never overwritten.
      let next=0;
      await Promise.all(Array.from({length:Math.min(3,pending.length)},async()=>{
        while(next<pending.length && current.current===userId && navigator.onLine) {
          const s=pending[next++];
          try { await drain(s.id,userId,2); }
          catch(e) { await recordSyncError(s.id,e instanceof Error ? e.message : String(e)).catch(()=>undefined); }
          await refreshOverview();
        }
      }));
    } catch {
      // Storage failures are distinguished by the refresh below. Failed owner
      // lookup leaves unverified legacy records intact and visibly unresolved.
    } finally {
      running.current.delete(userId);
      await refreshOverview();
      if(again.current.delete(userId) && current.current===userId)void run();
    }
  }, [userId,refreshOverview]);

  useEffect(() => {
    if (!userId) return;
    const kick = () => { void run(); };
    // A remote acknowledgement/error must update status without starting an
    // upload feedback loop between tabs. Online/foreground/timer still drain.
    const changedElsewhere = () => { void run(false); };
    const onVisible = () => { if (document.visibilityState === "visible") kick(); };
    kick();
    const timer = setInterval(kick, 30000);
    addEventListener("online", kick);
    addEventListener("field-observation-queue", changedElsewhere);
    addEventListener("field-observation-queue-remote", changedElsewhere);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      removeEventListener("online", kick);
      removeEventListener("field-observation-queue", changedElsewhere);
      removeEventListener("field-observation-queue-remote", changedElsewhere);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [userId, run]);

  // Do not render the previous account's saved-session details in the first
  // render before the identity-change effect clears its overview.
  return [current.current===userId ? overview : EMPTY, () => { void run(); }];
}
